import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const EditorialMethod = () => {
  const { language } = useLanguage();

  const methodSteps = [
    {
      number: '01',
      title: language === 'es' ? 'Destilar' : 'Distill',
      description: language === 'es' 
        ? 'Despojamos tu marca hasta su esencia—las verdades, tensiones y oportunidades que realmente importan.'
        : 'We strip your brand down to its core — the truths, tensions, and opportunities that actually matter.'
    },
    {
      number: '02',
      title: language === 'es' ? 'Diseñar' : 'Design',
      description: language === 'es'
        ? 'De la identidad a las campañas, creamos sistemas que se ven como arte y funcionan como estrategia.'
        : 'From identity to campaigns, we craft systems that look like art and work like strategy.'
    },
    {
      number: '03',
      title: language === 'es' ? 'Disrumpir' : 'Disrupt',
      description: language === 'es'
        ? 'Lanzamos con impacto. Lo suficientemente audaz para ser notado, lo suficientemente inteligente para perdurar.'
        : 'We launch with impact. Loud enough to get noticed, smart enough to stick.'
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-stone-900 text-white" data-testid="editorial-method">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header Grid */}
        <div className="grid lg:grid-cols-2 gap-16 mb-20">
          {/* Left - Main Title */}
          <div>
            <p className="text-terracota text-sm font-bold tracking-[0.3em] uppercase mb-6">
              {language === 'es' ? 'Nuestro Método' : 'Our Method'}
            </p>
            <h2 className="font-serif text-6xl md:text-7xl lg:text-8xl leading-[0.9]">
              <span className="italic text-stone-500">strategy</span>
              <br />
              <span className="text-white text-5xl md:text-6xl">&</span>
              <br />
              <span className="text-white">style</span>
            </h2>
          </div>

          {/* Right - Description */}
          <div className="flex items-end">
            <p className="text-stone-400 text-xl md:text-2xl leading-relaxed">
              {language === 'es'
                ? 'No creemos en ideas seguras, estrategias de plantilla o marketing de talla única. Nuestros servicios están diseñados para hacer que tu marca se sienta inevitable—desde cómo se ve, hasta cómo se recuerda.'
                : "We don't believe in safe ideas, templated strategies, or one-size-fits-all marketing. Our services are designed to make your brand feel inevitable — from the way it's seen, to the way it's remembered."}
            </p>
          </div>
        </div>

        {/* Method Steps - Reverse Order Display */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {methodSteps.slice().reverse().map((step, index) => (
            <div 
              key={step.number}
              className="relative p-8 border-t border-stone-700 group hover:border-terracota transition-colors"
              data-testid={`method-step-${step.number}`}
            >
              {/* Large Number Background */}
              <span className="absolute top-4 right-4 text-8xl font-serif text-stone-800 group-hover:text-stone-700 transition-colors select-none">
                {step.number}
              </span>
              
              {/* Content */}
              <div className="relative z-10">
                <h3 className="font-serif text-3xl md:text-4xl text-white mb-4 group-hover:text-terracota transition-colors">
                  {step.title}
                </h3>
                <p className="text-stone-400 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/contact">
            <button 
              className="inline-flex items-center gap-3 bg-terracota text-white px-10 py-5 text-base font-semibold uppercase tracking-wider hover:bg-white hover:text-stone-900 transition-all duration-300 rounded-full"
              data-testid="method-cta"
            >
              {language === 'es' ? 'Comenzar Proyecto' : 'Get Started'}
              <ArrowRight className="w-5 h-5" />
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default EditorialMethod;
